import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCurriculum } from "@/context/CurriculumContext";
import { SUBJECT_META_ALL } from "@/lib/scale";
import ContinuumTriangle from "@/components/ContinuumTriangle";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Sparkles, Save, Sprout, Search, ClipboardCheck,
  Target as TargetIcon, RefreshCw, Plus, FileText, CheckCircle2,
  TrendingUp, TrendingDown, Minus, ArrowUpCircle, History, Download, Wand2, Send,
} from "lucide-react";

const C = { green: "#7FB069", greenTint: "#B3E5A1", slate: "#44546A", grey: "#F2F2F2" };

const LEVELS = [
  { key: "classroom", label: "Classroom Support", sub: "All / most pupils", tint: "#81B29A" },
  { key: "school", label: "School Support", sub: "Some pupils (SET + teacher)", tint: "#6C8EBF" },
  { key: "school_plus", label: "School Support Plus", sub: "A few pupils (intensive)", tint: "#C0698D" },
];
const LEVEL_MAP = Object.fromEntries(LEVELS.map((l) => [l.key, l]));

const STEPS = [
  { key: "identify", label: "Identify", icon: Search, blurb: "Strengths, interests & needs" },
  { key: "assess", label: "Assess", icon: ClipboardCheck, blurb: "Clarify the concern" },
  { key: "plan", label: "Plan", icon: TargetIcon, blurb: "SMART targets & supports" },
  { key: "review", label: "Review", icon: RefreshCw, blurb: "Evaluate & decide next step" },
];

const CHECKLISTS = {
  basic_needs: { title: "Basic needs (Maslow)", items: ["Physiological (food, warmth, sleep)", "Safety & security", "Medical / sensory (hearing, vision, allergies)", "Belonging (family, friends, class)", "Esteem (respect, positive feedback)"] },
  support_checklist: { title: "Support checklist", items: ["Basic needs checklist completed", "Screening / assessment done", "Learning style observed", "Behaviour observed", "Pupil interviewed", "Classwork differentiated", "Learning environment adapted", "Yard / school environment adapted", "Outside professionals consulted", "Advice from SET / other staff", "Other interventions in place"] },
};

const DECISIONS = [
  { key: "continue", label: "Continue at current level", icon: Minus, tint: "#6C8EBF" },
  { key: "progress", label: "Progress to a higher level", icon: TrendingUp, tint: "#C0698D" },
  { key: "revert", label: "Revert to a lower level", icon: TrendingDown, tint: "#81B29A" },
  { key: "consult", label: "Request consultation", icon: Search, tint: "#E0A458" },
  { key: "close", label: "Close file (needs met)", icon: CheckCircle2, tint: "#7FB069" },
];
const DECISION_LABEL = {
  continue: "Continue at current level", progress: "Progress to a higher level",
  revert: "Revert to a lower level", consult: "Request consultation", close: "Close file (needs met)",
  maintain: "Continue at current level", step_up: "Progress to a higher level",
  step_down: "Revert to a lower level", fade: "Close file (needs met)",
};
const OUTCOMES = ["Achieved", "Partially achieved", "Not yet achieved"];
const FOCUS_AREAS = [
  { key: "literacy", label: "Literacy", tint: "#6C8EBF" },
  { key: "numeracy", label: "Numeracy", tint: "#E07A5F" },
  { key: "social_emotional", label: "Social & Emotional", tint: "#81B29A" },
  { key: "asdp", label: "ASDP", tint: "#9B7EDE" },
];

const uid = () => Math.random().toString(36).slice(2, 10);
const withKeys = (arr) => (arr || []).map((t) => (t._k ? t : { ...t, _k: uid() }));
const BAND_LABEL = { well_below: "Well below", early_developing: "Early developing", developing: "Developing", meeting: "Meeting", above: "Above" };
const isEmptyPlan = (p) => !p.strengths && !p.interests && !p.needs && !p.priority_needs && !p.barriers && !(p.targets || []).length;

export default function CosPlan() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const curriculum = useCurriculum();
  const [student, setStudent] = useState(null);
  const [plan, setPlan] = useState(null);
  const [context, setContext] = useState({ bands: [], gaps: [], targets: [] });
  const [autofill, setAutofill] = useState(null);
  const [reviewTargets, setReviewTargets] = useState([]);
  const [tests, setTests] = useState([]);
  const [log, setLog] = useState([]);
  const [prefilled, setPrefilled] = useState(false);
  const [step, setStep] = useState("identify");
  const [saving, setSaving] = useState(false);
  const [aiStep, setAiStep] = useState("");
  const [aiField, setAiField] = useState("");

  const load = useCallback(() => {
    api.get(`/students/${studentId}/cos`).then((r) => {
      const p = { ...r.data.plan, targets: withKeys(r.data.plan.targets) };
      const a = r.data.autofill || null;
      setStudent(r.data.student);
      setContext(r.data.context);
      setAutofill(a);
      setReviewTargets(r.data.review_targets || []);
      setTests(r.data.tests || []);
      setLog(r.data.log || []);
      if (a && isEmptyPlan(p)) {
        setPlan({
          ...p, strengths: a.strengths || "", interests: a.interests || "", needs: a.needs || "",
          priority_needs: a.priority_needs || "", barriers: a.barriers || "", info_sources: a.info_sources || "",
          targets: withKeys(a.targets || []),
        });
        setPrefilled(true);
      } else {
        setPlan(p);
      }
    }).catch(() => navigate("/"));
  }, [studentId, navigate]);
  useEffect(() => { load(); }, [load]);

  const set = (f, v) => setPlan((p) => ({ ...p, [f]: v }));

  const refreshDerived = async () => {
    const { data } = await api.get(`/students/${studentId}/cos`);
    setContext(data.context); setAutofill(data.autofill || null);
    setReviewTargets(data.review_targets || []); setTests(data.tests || []); setLog(data.log || []);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { reviews, ...body } = plan;
      await api.put(`/students/${studentId}/cos`, body);
      toast.success("Support plan saved");
      setPrefilled(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // Pull a field's value straight from the pupil's records (deterministic autofill).
  const pull = (field) => {
    if (!autofill) return;
    if (field === "targets") {
      const have = new Set((plan.targets || []).map((t) => t.source_target_id).filter(Boolean));
      const add = (autofill.targets || []).filter((t) => !t.source_target_id || !have.has(t.source_target_id));
      set("targets", [...(plan.targets || []), ...withKeys(add)]);
      toast.success("Priority targets pulled in");
      return;
    }
    set(field, autofill[field] || "");
    toast.success("Filled from records");
  };

  // Per-field AI suggestion. onText receives the generated text.
  const suggestField = async (field, { onText, focus = "", target = null, key = field } = {}) => {
    setAiField(key);
    try {
      const { data } = await api.post(`/students/${studentId}/cos/suggest-field`, { field, focus, target });
      onText(data.text || "");
      toast.success("AI suggestion added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiField(""); }
  };

  const draft = async (which, focus = "") => {
    setAiStep(which);
    try {
      const apiStep = which === "review" ? "review" : "plan";
      const { data } = await api.post(`/suggest/cos/${studentId}`, { step: apiStep, focus });
      const c = data.content || {};
      if (which !== "review") {
        setPlan((p) => {
          if (which === "identify") return { ...p, strengths: p.strengths || c.strengths || "", needs: p.needs || c.concerns || "" };
          if (which === "assess") return { ...p, priority_needs: p.priority_needs || c.concerns || "" };
          return {
            ...p,
            strengths: p.strengths || c.strengths || "",
            priority_needs: p.priority_needs || c.concerns || "",
            targets: [...(p.targets || []), ...(c.targets || []).map((t) => ({
              _k: uid(), subject: t.subject || "", area: t.area || "", target: t.target || "",
              strategies: t.strategies || "", resources: t.resources || "", responsibility: "",
            }))],
          };
        });
      }
      toast.success("AI draft added — review and edit");
      return c;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
    finally { setAiStep(""); }
  };

  const subjName = (k) => SUBJECT_META_ALL[k]?.name || k;
  const domName = (subj, dom) => curriculum?.subjects?.[subj]?.domains.find((d) => d.key === dom)?.name || dom;

  const addTargetFromSource = (t) => {
    if ((plan.targets || []).some((x) => x.source_target_id === t.id)) { toast.info("Already added"); return; }
    set("targets", [...(plan.targets || []), {
      _k: uid(), subject: subjName(t.subject), area: domName(t.subject, t.domain), target: t.text,
      strategies: t.strategies || "", resources: t.resources || "", responsibility: "", source_target_id: t.id,
    }]);
    toast.success("Target added to plan");
  };
  const addGapAsNeed = (g) => {
    const line = `${subjName(g.subject)} — ${g.area} (level ${g.level})`;
    set("priority_needs", plan.priority_needs ? `${plan.priority_needs}\n${line}` : line);
    toast.success("Added to priority needs");
  };

  if (!plan || !student)
    return <div className="min-h-screen flex justify-center items-center text-slate-300"><Loader2 className="animate-spin" /></div>;

  const lvl = LEVEL_MAP[plan.level] || LEVELS[0];
  const fieldProps = { pull, suggestField, aiField, autofill };
  const areaOpts = FOCUS_AREAS.filter((a) => a.key !== "asdp" || student.is_asd);
  const effectiveFocus = (plan.focus_areas && plan.focus_areas.length) ? plan.focus_areas : areaOpts.map((a) => a.key);

  const toggleFocus = async (key) => {
    let next = effectiveFocus.includes(key) ? effectiveFocus.filter((k) => k !== key) : [...effectiveFocus, key];
    if (next.length === 0) next = [key];
    setPlan((p) => ({ ...p, focus_areas: next }));
    try {
      const { reviews, ...body } = { ...plan, focus_areas: next };
      await api.put(`/students/${studentId}/cos`, body);
      await refreshDerived();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const viewRecord = (reviewId) => navigate(`/student/${studentId}/cos/document/review?review=${reviewId}`);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      {/* Toolbar */}
      <div className="sticky top-0 z-30 bg-[#F9F9F7]/90 backdrop-blur-md border-b border-slate-200/60">
        <div className="max-w-4xl mx-auto px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <button data-testid="cos-back-btn" onClick={() => navigate(`/student/${studentId}`)}
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
            <ArrowLeft size={16} /> Back to profile
          </button>
          <div className="flex items-center gap-2">
            <button data-testid="cos-doc-plan-btn" onClick={() => navigate(`/student/${studentId}/cos/document/plan`)}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-700 hover:border-[#7FB069] transition">
              <FileText size={16} /> Support Plan doc
            </button>
            <button data-testid="cos-doc-review-btn" onClick={() => navigate(`/student/${studentId}/cos/document/review`)}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-700 hover:border-[#7FB069] transition">
              <FileText size={16} /> Review doc
            </button>
            <button data-testid="cos-doc-full-btn" onClick={() => navigate(`/student/${studentId}/cos/document/full`)}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-700 hover:border-[#7FB069] transition">
              <Download size={16} /> Full file (PDF)
            </button>
            <button data-testid="cos-save-btn" onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-50"
              style={{ background: C.slate }}>
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 pt-6">
        {/* Header card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.05)] p-6">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="h-9 w-9 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: C.green }}><Sprout size={18} /></span>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-bold">Continuum of Support</p>
              <h1 className="font-['Outfit'] text-2xl font-bold text-slate-800 leading-tight">{student.name}</h1>
            </div>
            <p className="ml-auto text-sm text-slate-500">{student.class_level}{student.class_name ? ` · ${student.class_name}` : ""}</p>
          </div>

          {/* Level selector (Continuum of Support triangle) */}
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Level of support — tap a tier</p>
          <div data-testid="cos-level-selector">
            <ContinuumTriangle level={plan.level} onSelect={(k) => set("level", k)} />
          </div>
          <div className="flex items-center gap-6 mt-4 flex-wrap">
            <label className="text-sm">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">Next review date</span>
              <input data-testid="cos-review-date" type="date" value={plan.review_date || ""} onChange={(e) => set("review_date", e.target.value)}
                className="bg-[#F9F9F7] border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#7FB069]" />
            </label>
            {plan.status === "closed" && <span className="text-xs font-semibold rounded-full px-3 py-1 bg-slate-100 text-slate-500">Closed / needs met</span>}
          </div>

          {/* Areas of focus */}
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 mt-5">Areas we're working on</p>
          <div className="flex flex-wrap gap-2" data-testid="cos-focus-areas">
            {areaOpts.map((a) => {
              const on = effectiveFocus.includes(a.key);
              return (
                <button key={a.key} data-testid={`cos-focus-${a.key}`} onClick={() => toggleFocus(a.key)}
                  className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold font-['Outfit'] transition"
                  style={on ? { borderColor: a.tint, backgroundColor: `${a.tint}18`, color: a.tint } : { borderColor: "#e2e8f0", color: "#94a3b8" }}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: on ? a.tint : "#cbd5e1" }} />
                  {a.label}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">You may assess a pupil across all areas but only actively work on some — deselect the areas you're not targeting.</p>
        </div>

        {/* Prefill banner */}
        {prefilled && (
          <div data-testid="cos-prefill-banner" className="mt-4 rounded-2xl border border-[#7FB069]/40 bg-[#B3E5A1]/15 p-4 flex items-start gap-3">
            <span className="h-8 w-8 rounded-lg bg-[#7FB069] text-white flex items-center justify-center shrink-0"><Wand2 size={16} /></span>
            <div className="text-sm text-slate-700">
              <p className="font-semibold font-['Outfit'] text-[#4a6b45]">Pre-filled from {student.name.split(" ")[0]}'s records</p>
              <p className="text-slate-500 mt-0.5">We've drafted this plan from their assessments, targets and self-evaluation. Review, tweak anything, then <b>Save</b>. Use <Sparkles size={12} className="inline -mt-0.5" /> for AI wording or ↓ to re-pull any field.</p>
            </div>
          </div>
        )}

        {/* Stepper */}
        <div className="flex gap-2 mt-5 overflow-x-auto pb-1" data-testid="cos-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = step === s.key;
            return (
              <button key={s.key} data-testid={`cos-step-${s.key}`} onClick={() => setStep(s.key)}
                className="flex-1 min-w-[150px] rounded-xl border p-3 text-left transition"
                style={active ? { borderColor: C.green, backgroundColor: "#fff", boxShadow: `0 0 0 1px ${C.green}` } : { borderColor: "#e2e8f0", backgroundColor: "#fff" }}>
                <div className="flex items-center gap-2">
                  <span className="h-7 w-7 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: active ? C.green : "#cbd5e1" }}>{i + 1}</span>
                  <span className="font-semibold font-['Outfit'] text-slate-800 flex items-center gap-1.5"><Icon size={15} /> {s.label}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1.5">{s.blurb}</p>
              </button>
            );
          })}
        </div>

        {/* Step body */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.05)] p-6 mt-4">
          {step === "identify" && <Identify plan={plan} set={set} context={context} subjName={subjName} addGapAsNeed={addGapAsNeed} draft={draft} aiStep={aiStep} fieldProps={fieldProps} />}
          {step === "assess" && <Assess plan={plan} set={set} draft={draft} aiStep={aiStep} fieldProps={fieldProps} tests={tests} />}
          {step === "plan" && (
            <PlanStep plan={plan} set={set} context={context} subjName={subjName} domName={domName}
              addTargetFromSource={addTargetFromSource} draft={draft} aiStep={aiStep} fieldProps={fieldProps} />
          )}
          {step === "review" && <ReviewStep plan={plan} studentId={studentId} reload={load} draft={draft} aiStep={aiStep} reviewTargets={reviewTargets} fieldProps={fieldProps} />}
        </div>

        {/* Log of Actions */}
        <LogOfActions log={log} viewRecord={viewRecord} />
      </div>
    </div>
  );
}

/* ---------------- Log of Actions ---------------- */
const LOG_ICON = { created: Sprout, targets: TargetIcon, sent: Send, signed: CheckCircle2, review: History };
function LogOfActions({ log, viewRecord }) {
  if (!log || log.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.05)] p-6 mt-4" data-testid="cos-log">
      <p className="text-sm font-semibold text-slate-600 font-['Outfit'] flex items-center gap-1.5 mb-4"><History size={16} /> Log of actions</p>
      <div className="relative pl-6 space-y-4 before:absolute before:left-[7px] before:top-1 before:bottom-1 before:w-px before:bg-slate-200">
        {log.map((e, i) => {
          const Icon = LOG_ICON[e.type] || History;
          return (
            <div key={e.review_id || `${e.date}-${e.action}`} data-testid={`cos-log-${i}`} className="relative">
              <span className="absolute -left-6 top-0.5 h-4 w-4 rounded-full bg-white border-2 border-[#7FB069] flex items-center justify-center">
                <Icon size={9} className="text-[#7FB069]" />
              </span>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm text-slate-700">{e.action}</p>
                  {e.date && <p className="text-xs text-slate-400">{e.date}</p>}
                </div>
                {e.type === "review" && e.review_id && (
                  <button data-testid={`cos-log-view-${i}`} onClick={() => viewRecord(e.review_id)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-slate-200 bg-white px-3 py-1.5 text-slate-600 hover:border-[#7FB069] hover:text-[#5d8a5a] transition">
                    <FileText size={13} /> View full record
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Assessment feed ---------------- */
function AssessmentFeed({ context, subjName, addGapAsNeed }) {
  return (
    <div className="rounded-xl border border-dashed border-[#7FB069]/40 bg-[#B3E5A1]/10 p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-[#5d8a5a] flex items-center gap-1.5 mb-2.5">
        <TrendingUp size={13} /> From this pupil's assessments
      </p>
      {context.bands.length === 0 && <p className="text-sm text-slate-400">No assessments recorded yet.</p>}
      <div className="flex flex-wrap gap-2 mb-3">
        {context.bands.map((b) => {
          const meta = SUBJECT_META_ALL[b.subject];
          return (
            <span key={b.subject} className="inline-flex items-center gap-1.5 text-xs rounded-full bg-white border px-3 py-1.5"
              style={{ borderColor: `${meta?.tint || "#cbd5e1"}66` }}>
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta?.tint }} />
              <span className="font-semibold" style={{ color: meta?.tint }}>{subjName(b.subject)}</span>
              <span className="text-slate-500">{BAND_LABEL[b.band] || b.band} · {b.total}/45</span>
            </span>
          );
        })}
      </div>
      {context.gaps.length > 0 && (
        <>
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">Lowest areas (tap to add to needs)</p>
          <div className="flex flex-wrap gap-2">
            {context.gaps.map((g, i) => (
              <button key={`${g.subject}-${g.domain}`} data-testid={`cos-gap-${i}`} onClick={() => addGapAsNeed(g)}
                className="inline-flex items-center gap-1.5 text-xs rounded-full bg-white border border-slate-200 px-3 py-1.5 hover:border-[#7FB069] transition">
                <Plus size={12} className="text-slate-400" />
                <span className="text-slate-600">{subjName(g.subject)} · {g.area}</span>
                <span className="font-bold text-[#C0698D]">{g.level}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- AI draft bar (optional teacher steer) ---------------- */
function AiDraftBar({ which, aiStep, draft, placeholder }) {
  const [focus, setFocus] = useState("");
  const busy = aiStep === which;
  return (
    <div className="rounded-xl border border-[#7FB069]/30 bg-[#B3E5A1]/10 p-3" data-testid={`cos-ai-bar-${which}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-[#5d8a5a] flex items-center gap-1.5 mb-2">
        <Sparkles size={12} /> Draft with AI
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <input data-testid={`cos-ai-focus-${which}`} value={focus} onChange={(e) => setFocus(e.target.value)}
          placeholder={placeholder} disabled={busy}
          onKeyDown={(e) => { if (e.key === "Enter") draft(which, focus); }}
          className="flex-1 min-w-[200px] bg-white border border-slate-200 rounded-full px-4 py-2 text-sm text-slate-700 outline-none focus:border-[#7FB069] transition disabled:opacity-50" />
        <button data-testid={`cos-ai-${which}-btn`} onClick={() => draft(which, focus)} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-50 shrink-0"
          style={{ background: "linear-gradient(135deg,#7FB069,#5d8a5a)" }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate
        </button>
      </div>
      <p className="text-[11px] text-slate-400 mt-1.5">Optional — leave blank for a general draft, or steer it (e.g. area, skill, concern).</p>
    </div>
  );
}

/* ---------------- Steps ---------------- */
function Identify({ plan, set, context, subjName, addGapAsNeed, draft, aiStep, fieldProps }) {
  return (
    <div className="space-y-5">
      <AiDraftBar which="identify" aiStep={aiStep} draft={draft} placeholder="e.g. 'oral language & confidence' or 'fine-motor skills'" />
      <AssessmentFeed context={context} subjName={subjName} addGapAsNeed={addGapAsNeed} />
      <SmartField label="Strengths" field="strengths" testId="cos-strengths" value={plan.strengths} onChange={(v) => set("strengths", v)} {...fieldProps} />
      <SmartField label="Interests" field="interests" testId="cos-interests" value={plan.interests} onChange={(v) => set("interests", v)} rows={2} {...fieldProps} />
      <SmartField label="Needs (overview)" field="needs" testId="cos-needs" value={plan.needs} onChange={(v) => set("needs", v)} {...fieldProps} />
      <SmartField label="Information gathered from (teacher, parents, assessments, reports)" field="info_sources" testId="cos-info-sources" value={plan.info_sources} onChange={(v) => set("info_sources", v)} rows={2} {...fieldProps} />
    </div>
  );
}

function Assess({ plan, set, draft, aiStep, fieldProps, tests }) {
  const toggle = (group, item) => {
    const cur = new Set((plan.checklists?.[group]) || []);
    cur.has(item) ? cur.delete(item) : cur.add(item);
    set("checklists", { ...(plan.checklists || {}), [group]: [...cur] });
  };
  return (
    <div className="space-y-5">
      <AiDraftBar which="assess" aiStep={aiStep} draft={draft} placeholder="e.g. 'phonics blending' or 'attention during independent work'" />
      <SmartField label="Priority needs / areas of concern" field="priority_needs" testId="cos-priority-needs" value={plan.priority_needs} onChange={(v) => set("priority_needs", v)} rows={3} {...fieldProps} />
      <SmartField label="Possible reasons for concerns" field="possible_reasons" testId="cos-possible-reasons" value={plan.possible_reasons} onChange={(v) => set("possible_reasons", v)} rows={2} {...fieldProps} />
      <SmartField label="Barriers to learning" field="barriers" testId="cos-barriers" value={plan.barriers} onChange={(v) => set("barriers", v)} rows={2} {...fieldProps} />

      {/* Standardised tests (read-only, pulled from records) */}
      {(tests || []).length > 0 && (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="cos-tests">
          <p className="text-sm font-semibold text-slate-600 font-['Outfit'] mb-1">Standardised & diagnostic tests</p>
          <p className="text-xs text-slate-400 mb-3">Pulled automatically from this pupil's test records.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="py-1.5 pr-3">Date</th><th className="py-1.5 pr-3">Assessment</th>
                <th className="py-1.5 pr-3">Result</th><th className="py-1.5">Risk band</th>
              </tr></thead>
              <tbody>
                {tests.map((t) => (
                  <tr key={`${t.date}-${t.name}`} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3 text-slate-500">{t.date || "—"}</td>
                    <td className="py-1.5 pr-3 text-slate-700">{t.name}</td>
                    <td className="py-1.5 pr-3 text-slate-700">{t.result || "—"}</td>
                    <td className="py-1.5 text-slate-500">{t.risk_band || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <p className="text-sm font-semibold text-slate-600 font-['Outfit'] mb-2">Basic needs & support checklists <span className="text-slate-400 font-normal">(optional)</span></p>
        <div className="grid sm:grid-cols-2 gap-3">
          {Object.entries(CHECKLISTS).map(([group, cfg]) => (
            <div key={group} className="rounded-xl border border-slate-100 p-3.5" data-testid={`cos-checklist-${group}`}>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{cfg.title}</p>
              <div className="space-y-1.5">
                {cfg.items.map((item) => {
                  const on = ((plan.checklists?.[group]) || []).includes(item);
                  return (
                    <label key={item} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={on} onChange={() => toggle(group, item)} className="h-4 w-4 accent-[#7FB069]" />
                      <span className={on ? "text-slate-700" : "text-slate-500"}>{item}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <SmartField label="Possible actions to meet basic needs" field="basic_needs_actions" testId="cos-basic-needs-actions" value={plan.basic_needs_actions} onChange={(v) => set("basic_needs_actions", v)} rows={2} {...fieldProps} />
    </div>
  );
}

function PlanStep({ plan, set, context, subjName, domName, addTargetFromSource, draft, aiStep, fieldProps }) {
  const upd = (i, f, v) => set("targets", plan.targets.map((t, idx) => (idx === i ? { ...t, [f]: v } : t)));
  const del = (i) => set("targets", plan.targets.filter((_, idx) => idx !== i));
  const addBlank = () => set("targets", [...(plan.targets || []), { _k: uid(), subject: "", area: "", target: "", strategies: "", resources: "", responsibility: "" }]);
  const chosen = new Set((plan.targets || []).map((t) => t.source_target_id).filter(Boolean));
  const available = (context.targets || []).filter((t) => !chosen.has(t.id));
  const { suggestField, aiField } = fieldProps;
  const tCtx = (t) => ({ subject: t.subject, area: t.area, target: t.target });

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-100 bg-[#F9F9F7] p-3.5">
        <p className="text-sm font-semibold text-slate-600 font-['Outfit']">Targets, strategies & supports</p>
        <p className="text-xs text-slate-400 mt-0.5">The pupil's priority learning targets are carried over from their profile. Add or remove any below — new targets aren't invented here.</p>
      </div>

      {available.length > 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-3.5">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Plus size={13} /> Add a priority target</p>
          <div className="flex flex-wrap gap-2">
            {available.map((t) => {
              const meta = SUBJECT_META_ALL[t.subject];
              return (
                <button key={t.id} data-testid={`cos-pick-target-${t.id}`} onClick={() => addTargetFromSource(t)}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full border bg-white px-3 py-1.5 hover:shadow-sm transition"
                  style={{ borderColor: `${meta?.tint || "#cbd5e1"}66` }}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta?.tint }} />
                  <span className="font-semibold" style={{ color: meta?.tint }}>{subjName(t.subject)}</span>
                  <span className="text-slate-500 max-w-[200px] truncate">{t.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {(plan.targets || []).map((t, i) => (
          <div key={t._k || i} data-testid={`cos-target-${i}`} className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold font-['Outfit']" style={{ color: C.slate }}>{t.subject && t.area ? `${t.subject} — ${t.area}` : `Target ${i + 1}`}</span>
              <button data-testid={`cos-target-remove-${i}`} onClick={() => del(i)} className="text-xs text-red-400 hover:text-red-600">Remove</button>
            </div>
            <Mini label="Subject" value={t.subject} onChange={(v) => upd(i, "subject", v)} />
            <Mini label="Skill area" value={t.area} onChange={(v) => upd(i, "area", v)} />
            <MiniArea label="SMART target" value={t.target} onChange={(v) => upd(i, "target", v)} />
            <MiniArea label="Strategies / interventions" value={t.strategies} onChange={(v) => upd(i, "strategies", v)}
              onAi={() => suggestField("strategies", { key: `strategies-${i}`, target: tCtx(t), onText: (x) => upd(i, "strategies", x) })}
              busy={aiField === `strategies-${i}`} testId={`cos-ai-strategies-${i}`} />
            <div className="grid sm:grid-cols-2 gap-3">
              <MiniArea label="Resources" value={t.resources} onChange={(v) => upd(i, "resources", v)} rows={1}
                onAi={() => suggestField("resources", { key: `resources-${i}`, target: tCtx(t), onText: (x) => upd(i, "resources", x) })}
                busy={aiField === `resources-${i}`} testId={`cos-ai-resources-${i}`} />
              <Mini label="Responsibility (who)" value={t.responsibility} onChange={(v) => upd(i, "responsibility", v)} />
            </div>
          </div>
        ))}
        <button data-testid="cos-add-target" onClick={addBlank}
          className="text-sm font-semibold rounded-lg border border-dashed px-4 py-2 transition hover:bg-slate-50" style={{ borderColor: C.green, color: C.green }}>
          + Add blank target
        </button>
      </div>

      <SmartField label="Staff involved & resources needed" field="personnel" testId="cos-personnel" value={plan.personnel} onChange={(v) => set("personnel", v)} rows={2} {...fieldProps} />
      <SmartField label="Additional notes / home-school links" field="notes" testId="cos-notes" value={plan.notes} onChange={(v) => set("notes", v)} rows={2} {...fieldProps} />
    </div>
  );
}

function ReviewStep({ plan, studentId, reload, aiStep, reviewTargets, fieldProps }) {
  const empty = { present: "", most_successful: "", concerns_changed: "", needs_changed: "", future_actions: "", student_comment: "", parent_comment: "", decision: "continue", next_review_date: "" };
  const [rev, setRev] = useState(empty);
  const [outcomes, setOutcomes] = useState(() => (reviewTargets || []).map((t) => ({ ...t })));
  const [saving, setSaving] = useState(false);
  const setF = (f, v) => setRev((r) => ({ ...r, [f]: v }));
  const setOut = (i, f, v) => setOutcomes((o) => o.map((x, idx) => (idx === i ? { ...x, [f]: v } : x)));
  const { suggestField, aiField } = fieldProps;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/students/${studentId}/cos/reviews`, { ...rev, target_outcomes: outcomes });
      toast.success("Review recorded — support level updated");
      setRev(empty);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm font-semibold text-slate-600 font-['Outfit']">Support review record</p>
        <p className="text-xs text-slate-400 mt-0.5">A full copy of this record is saved to the log so you can reopen it any time.</p>
      </div>

      <SmartField label="Names of those present at review" field="present" testId="cos-review-present" value={rev.present} onChange={(v) => setF("present", v)} rows={1} suggestField={suggestField} aiField={aiField} />

      {outcomes.length > 0 && (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="cos-review-outcomes">
          <p className="text-sm font-semibold text-slate-600 font-['Outfit'] mb-1">Target outcomes</p>
          <p className="text-xs text-slate-400 mb-3">Pre-filled from each target's tracked progress — confirm or adjust.</p>
          <div className="space-y-3">
            {outcomes.map((o, i) => (
              <div key={`${o.subject}-${o.area}-${i}`} data-testid={`cos-outcome-${i}`} className="rounded-lg bg-[#F9F9F7] border border-slate-100 p-3">
                <p className="text-xs font-bold" style={{ color: C.slate }}>{o.subject}{o.area ? ` — ${o.area}` : ""}</p>
                <p className="text-sm text-slate-600 mt-0.5 mb-2">{o.target}</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {OUTCOMES.map((op) => {
                    const on = o.outcome === op;
                    return (
                      <button key={op} data-testid={`cos-outcome-${i}-${op.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setOut(i, "outcome", op)}
                        className="text-xs font-semibold rounded-full px-3 py-1 border transition"
                        style={on ? { borderColor: C.green, backgroundColor: `${C.green}15`, color: "#4a6b45" } : { borderColor: "#e2e8f0", color: "#64748b" }}>{op}</button>
                    );
                  })}
                </div>
                <input data-testid={`cos-outcome-evidence-${i}`} value={o.evidence || ""} onChange={(e) => setOut(i, "evidence", e.target.value)}
                  placeholder="Evidence / observations (optional)"
                  className="w-full bg-white border border-slate-100 rounded-lg px-3 py-1.5 text-sm text-slate-700 outline-none focus:border-[#7FB069] transition" />
              </div>
            ))}
          </div>
        </div>
      )}

      <SmartField label="What areas of the plan were most successful, and why?" field="most_successful" testId="cos-review-successful" value={rev.most_successful} onChange={(v) => setF("most_successful", v)} rows={2} suggestField={suggestField} aiField={aiField} />
      <SmartField label="Since the start of the plan, has anything changed regarding the original concerns?" field="concerns_changed" testId="cos-review-concerns" value={rev.concerns_changed} onChange={(v) => setF("concerns_changed", v)} rows={2} suggestField={suggestField} aiField={aiField} />
      <SmartField label="Have the pupil's needs changed since the start of the plan, and if so how?" field="needs_changed" testId="cos-review-needs" value={rev.needs_changed} onChange={(v) => setF("needs_changed", v)} rows={2} suggestField={suggestField} aiField={aiField} />
      <SmartField label="Recommended future actions — what, how, who, when?" field="future_actions" testId="cos-review-actions" value={rev.future_actions} onChange={(v) => setF("future_actions", v)} rows={2} suggestField={suggestField} aiField={aiField} />
      <div className="grid sm:grid-cols-2 gap-4">
        <SmartField label="Comments from the pupil" field="student_comment" testId="cos-review-student" value={rev.student_comment} onChange={(v) => setF("student_comment", v)} rows={2} suggestField={suggestField} aiField={aiField} />
        <SmartField label="Comments from parent(s)/guardian(s)" field="parent_comment" testId="cos-review-parent" value={rev.parent_comment} onChange={(v) => setF("parent_comment", v)} rows={2} suggestField={suggestField} aiField={aiField} />
      </div>

      <div>
        <p className="text-sm font-semibold text-slate-600 font-['Outfit'] mb-2">Outcome of review</p>
        <div className="grid sm:grid-cols-3 gap-2" data-testid="cos-decision">
          {DECISIONS.map((d) => {
            const Icon = d.icon;
            const active = rev.decision === d.key;
            return (
              <button key={d.key} data-testid={`cos-decision-${d.key}`} onClick={() => setF("decision", d.key)}
                className="rounded-xl border p-3 text-center transition"
                style={active ? { borderColor: d.tint, backgroundColor: `${d.tint}12`, boxShadow: `0 0 0 1px ${d.tint}` } : { borderColor: "#e2e8f0" }}>
                <Icon size={18} className="mx-auto mb-1" style={{ color: active ? d.tint : "#94a3b8" }} />
                <span className="text-xs font-semibold" style={{ color: active ? d.tint : "#475569" }}>{d.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <label className="text-sm block">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">Set next review date</span>
        <input data-testid="cos-review-next-date" type="date" value={rev.next_review_date} onChange={(e) => setF("next_review_date", e.target.value)}
          className="bg-[#F9F9F7] border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#7FB069]" />
      </label>
      <button data-testid="cos-submit-review-btn" onClick={submit} disabled={saving}
        className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-semibold font-['Outfit'] text-white transition disabled:opacity-50" style={{ background: C.slate }}>
        {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Record review
      </button>
    </div>
  );
}

/* ---------------- shared inputs ---------------- */
function FieldActions({ field, testId, canPull, onPull, onAi, busy }) {
  return (
    <div className="flex items-center gap-1">
      {canPull && (
        <button data-testid={`cos-pull-${field}`} onClick={onPull} title="Fill from this pupil's records"
          className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-500 hover:border-[#7FB069] hover:text-[#5d8a5a] transition">
          <Download size={12} /> Records
        </button>
      )}
      <button data-testid={testId} onClick={onAi} disabled={busy} title="AI suggestion"
        className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 text-white transition disabled:opacity-50"
        style={{ background: "linear-gradient(135deg,#7FB069,#5d8a5a)" }}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI
      </button>
    </div>
  );
}

function SmartField({ label, field, value, onChange, rows = 3, testId, suggestField, aiField, autofill }) {
  const canPull = autofill && (field in autofill) && !!autofill[field];
  return (
    <div className="block">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-sm font-semibold text-slate-600 font-['Outfit']">{label}</span>
        <FieldActions field={field} testId={`cos-ai-${field}`} canPull={canPull}
          onPull={() => onChange(autofill[field])}
          onAi={() => suggestField(field, { onText: onChange })} busy={aiField === field} />
      </div>
      <textarea data-testid={testId} rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#F9F9F7] border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm text-slate-700 outline-none focus:border-[#7FB069] focus:bg-white resize-none transition" />
    </div>
  );
}

function Mini({ label, value, onChange }) {
  return (
    <div className="mt-2">
      <p className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-1">{label}</p>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#F9F9F7] border border-slate-100 rounded-lg px-3 py-1.5 text-sm text-slate-700 outline-none focus:border-[#7FB069] focus:bg-white transition" />
    </div>
  );
}
function MiniArea({ label, value, onChange, rows = 2, onAi, busy, testId }) {
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">{label}</p>
        {onAi && (
          <button data-testid={testId} onClick={onAi} disabled={busy} title="AI suggestion"
            className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 text-white transition disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#7FB069,#5d8a5a)" }}>
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} AI
          </button>
        )}
      </div>
      <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#F9F9F7] border border-slate-100 rounded-lg px-3 py-1.5 text-sm text-slate-700 outline-none focus:border-[#7FB069] focus:bg-white resize-none transition" />
    </div>
  );
}
